import { Checkbox, DefaultButton, MessageBar, MessageBarType, Panel, PanelType, Stack } from '@fluentui/react';
import React from 'react';
import { useGetInstalledExtensionsQuery, useGetItemDetailsQuery, useLazyGetContactByIdQuery } from '../../../store/Api';
import { INormalizedProduct } from '../../../models/NormalizedProduct';
import VendorDisplay from '../../Products/VendorDisplay';
import { EXTENSION_TYPE_PROCUREMENT } from '../../../models/Extension';
import AccordionBox from '../../Common/AccordionBox';
import NumberField from '../../Inputs/NumberField';
import { ICreateProcurementOrderRequest, IProcurementCompany } from '../../../models/Procurement';
import CompanyForm from './CompanyForm';
import { useAddProcurementDemandItemToOrderMutation, useCreateProcurementDemandItemMutation, useCreateProcurementOrderMutation, useGetProcurementOrganizationSettingsQuery } from '../../../store/endpoints/ProcurementEndpoints';
import ToggleField from '../../Common/ToggleField';
import ExistingOrderSearch from './ExistingOrderSearch';
import OptionSelectionToggle from '../../Common/OptionSelectionToggle';
import DataField from '../../Common/DataField';

interface IProcureProductPanelProps {
    catalog: string;
    productId: string;
    vendor?: string;
    onDismiss: () => void;
    onProcured: (demandId: string, orderId?: string) => void;
}

// Don't send an all-empty company object — treat "nothing entered" as no details.
const nonEmptyCompany = (company: IProcurementCompany): IProcurementCompany | undefined =>
    Object.values(company).some(v => v !== undefined && v !== null && v !== '') ? company : undefined;

const ProcureProductPanel: React.FC<IProcureProductPanelProps> = (props) => {
    const product = useGetItemDetailsQuery({ catalog: props.catalog, id: props.productId });
    const [selectedVendor, setSelectedVendor] = React.useState<string | undefined>(props.vendor);
    const [existingOrder, setExistingOrder] = React.useState<string | undefined>(undefined);
    const [createDemand] = useCreateProcurementDemandItemMutation();
    const [createOrder] = useCreateProcurementOrderMutation();
    const [addToExistingOrder] = useAddProcurementDemandItemToOrderMutation();
    const [procurementMethod, setProcurementMethod] = React.useState<string>(PROCUREMENT_METHOD_DEMAND);
    const [order, setOrder] = React.useState<Partial<ICreateProcurementOrderRequest>>({});
    const [quantity, setQuantity] = React.useState<number | undefined>(1);
    const [userDetails, setUserDetails] = React.useState<IProcurementCompany>({});
    const [shippingDetails, setShippingDetails] = React.useState<IProcurementCompany>({});
    const [error, setError] = React.useState<string | undefined>(undefined);
    const [processingMessage, setProcessingMessage] = React.useState<string | undefined>(undefined);

    const dismiss = () => {
        props.onDismiss();
    }

    const validate = (): boolean => {
        switch (procurementMethod) {
            case PROCUREMENT_METHOD_DEMAND:
                if (!quantity || !selectedVendor) return false;
                break;
            case PROCUREMENT_METHOD_ORDER:
                if (!quantity || !selectedVendor) return false;
                break;
            case PROCUREMENT_METHOD_EXISTING_ORDER:
                if (!quantity || !selectedVendor || !existingOrder) return false;
                break;
        }
        return true;
    }

    const procure = async () => {
        setError(undefined);
        if (!selectedVendor) {
            setError('Please select a vendor');
            return;
        }
        setProcessingMessage('Creating procurement demand item');
        const demandItemResult = await createDemand({
            item: {
                catalog: props.catalog,
                productId: props.productId,
                quantity: quantity && quantity > 0 ? quantity : 1,
                vendor: selectedVendor,
                endUser: nonEmptyCompany(userDetails),
                shipTo: nonEmptyCompany(shippingDetails),
            }
        })
        if (!('data' in demandItemResult) || !demandItemResult.data.id) {
            setError('Error creating procurement demand item');
            return;
        }
        if (procurementMethod === PROCUREMENT_METHOD_DEMAND) {
            setProcessingMessage(undefined);
            props.onProcured(demandItemResult.data.id);
            return;
        }
        if (procurementMethod === PROCUREMENT_METHOD_ORDER) {
            setProcessingMessage('Creating procurement order');
            const orderResult = await createOrder({
                order: {
                    vendor: selectedVendor,
                    ...order,
                    endUser: nonEmptyCompany(userDetails),
                    shipTo: nonEmptyCompany(shippingDetails),
                    items: [{
                        catalog: props.catalog,
                        productId: props.productId,
                        quantity: quantity && quantity > 0 ? quantity : 1,
                        procurementDemandId: demandItemResult.data.id,
                    }]
                }
            });
            setProcessingMessage(undefined);
            if (!('data' in orderResult) || !orderResult.data.id) {
                setError('Error creating procurement order');
                return;
            }
            props.onProcured(demandItemResult.data.id, orderResult.data.id);
        } else if (procurementMethod === PROCUREMENT_METHOD_EXISTING_ORDER) {
            setProcessingMessage('Adding item to existing order');
            const addResult = await addToExistingOrder({
                itemId: demandItemResult.data.id,
                orderId: existingOrder!,
                data: {
                    endUserDetails: userDetails
                }
            });
            setProcessingMessage(undefined);
            if (!('data' in addResult) || !addResult.data.id) {
                setError('Error adding item to existing order');
                return;
            }
            props.onProcured(demandItemResult.data.id, existingOrder);
        }
    }

    return (
        <Panel
            type={PanelType.medium}
            isOpen={true}
            onDismiss={dismiss}
            headerText="Procure Product"
            closeButtonAriaLabel="Close"
            isFooterAtBottom={true}
            onRenderFooter={() => <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: '1em' }} style={{ padding: '1em' }}>
                <DefaultButton style={{ flexGrow: 1 }} onClick={dismiss} disabled={processingMessage !== undefined}>Cancel</DefaultButton>
                <DefaultButton style={{ flexGrow: 1 }} primary onClick={procure} disabled={product.isLoading || !validate() || processingMessage !== undefined}>{processingMessage || 'Procure'}</DefaultButton>
            </Stack>}
        >
            {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar> }
            {product.isLoading
                ? <div>Loading...</div>
                : product.data ? <>
                    <h3>{product.data.name}</h3>
                    <div style={{ marginBottom: '1em' }}>
                        <OptionSelectionToggle
                            options={[
                                { key: PROCUREMENT_METHOD_DEMAND, text: PROCUREMENT_METHOD_FRIENDLY_NAMES[PROCUREMENT_METHOD_DEMAND] },
                                { key: PROCUREMENT_METHOD_ORDER, text: PROCUREMENT_METHOD_FRIENDLY_NAMES[PROCUREMENT_METHOD_ORDER] },
                                { key: PROCUREMENT_METHOD_EXISTING_ORDER, text: PROCUREMENT_METHOD_FRIENDLY_NAMES[PROCUREMENT_METHOD_EXISTING_ORDER] }
                            ]}
                            selectedKey={procurementMethod}
                            onChange={setProcurementMethod}
                        />
                    </div>
                    <ProcureProductPanelData
                        product={product.data}
                        vendor={props.vendor}
                        selectedVendor={selectedVendor}
                        onSelectVendor={setSelectedVendor}
                        existingOrder={existingOrder}
                        onSelectExistingOrder={setExistingOrder}
                        method={procurementMethod}
                        order={order}
                        onChangeOrder={setOrder}
                        quantity={quantity}
                        onChangeQuantity={setQuantity}
                        userDetails={userDetails}
                        onChangeUserDetails={setUserDetails}
                        shippingDetails={shippingDetails}
                        onChangeShippingDetails={setShippingDetails}
                    />
                </>
                    : <div>Unable to load product details.</div>
            }
        </Panel>
    );
}

interface IProcureProductPanelData {
    product: INormalizedProduct;
    vendor?: string;
    selectedVendor?: string;
    onSelectVendor: (vendor?: string) => void;
    existingOrder?: string;
    onSelectExistingOrder: (orderId?: string) => void;
    method: string;
    order: Partial<ICreateProcurementOrderRequest>;
    onChangeOrder: (order: Partial<ICreateProcurementOrderRequest>) => void;
    quantity?: number;
    onChangeQuantity: (quantity?: number) => void;
    userDetails: IProcurementCompany;
    onChangeUserDetails: (company: IProcurementCompany) => void;
    shippingDetails: IProcurementCompany;
    onChangeShippingDetails: (company: IProcurementCompany) => void;
}

const STEP_NONE = 'none';
const STEP_SET_QUANTITY = 'Set Quantity';
const STEP_SELECT_VENDOR = 'Select Vendor';
const STEP_PROCUREMENT_OPTIONS = 'Procurement Options';
const STEP_EXISTING_ORDERS = 'Existing Orders';
const STEP_USER_DETAILS = 'User Details';
const STEP_SHIPPING_DETAILS = 'Shipping Details';
const STEP_REVIEW = 'Review';
const STEP_ORDER_DETAILS = 'Order Details';

const CREATE_DEMAND_STEPS = [
    STEP_SET_QUANTITY,
    STEP_SELECT_VENDOR,
    STEP_USER_DETAILS,
    STEP_SHIPPING_DETAILS,
    STEP_REVIEW
]

const CREATE_ORDER_STEPS = [
    STEP_SET_QUANTITY,
    STEP_SELECT_VENDOR,
    STEP_USER_DETAILS,
    STEP_SHIPPING_DETAILS,
    STEP_ORDER_DETAILS,
    STEP_REVIEW
]

const EXISTING_ORDER_STEPS = [
    STEP_SET_QUANTITY,
    STEP_SELECT_VENDOR,
    STEP_EXISTING_ORDERS,
    STEP_USER_DETAILS,
    STEP_REVIEW
]

const PROCUREMENT_METHOD_DEMAND = 'demand';
const PROCUREMENT_METHOD_ORDER = 'order';
const PROCUREMENT_METHOD_EXISTING_ORDER = 'addToOrder';

const PROCUREMENT_METHOD_FRIENDLY_NAMES: { [key: string]: string } = {
    [PROCUREMENT_METHOD_DEMAND]: 'Create Demand',
    [PROCUREMENT_METHOD_ORDER]: 'Create Order',
    [PROCUREMENT_METHOD_EXISTING_ORDER]: 'Add to Order'
};

const ProcureProductPanelData: React.FC<IProcureProductPanelData> = (props) => {
    const installedExtensions = useGetInstalledExtensionsQuery();
    const [procurementVendors, setProcurementVendors] = React.useState<string[]>([]);
    const [step, setStep] = React.useState(STEP_SET_QUANTITY);
    const settings = useGetProcurementOrganizationSettingsQuery();
    const [getContactDetails] = useLazyGetContactByIdQuery();

    React.useEffect(() => {
        if (installedExtensions.data) {
            const vendors = installedExtensions.data.filter(ext => ext.extensionType === EXTENSION_TYPE_PROCUREMENT).map(ext => ext.vendor);
            setProcurementVendors(vendors);
        }
    }, [installedExtensions.data]);

    React.useEffect(() => {
        if (settings.data?.defaultShipmentContact && Object.keys(props.shippingDetails || {}).length === 0) {
            getContactDetails({ contactId: settings.data.defaultShipmentContact, includeCompanyDetails: true })
                .then((response) => {
                    if ('data' in response) {
                        const contact = response.data;
                        if (contact) {
                            props.onChangeShippingDetails?.({
                                contactId: contact.id,
                                companyName: contact.companyDetails?.companyName,
                                firstName: contact.firstName,
                                lastName: contact.lastName,
                                email: contact.emailAddress,
                                phone: contact.phoneNumber,
                                address1: contact.companyDetails?.address?.address1,
                                address2: contact.companyDetails?.address?.address2,
                                address3: contact.companyDetails?.address?.address3,
                                city: contact.companyDetails?.address?.city,
                                stateTerritory: contact.companyDetails?.address?.stateTerritory,
                                postalCodeZip: contact.companyDetails?.address?.postalCodeZip,
                                country: contact.companyDetails?.address?.country,
                            });
                        }
                    }
                });
        }
    }, [settings.data, props.shippingDetails]);

    React.useEffect(() => {
        // Reset step when method changes
        if (props.method === PROCUREMENT_METHOD_DEMAND) {
            setStep(CREATE_DEMAND_STEPS[0]);
        } else if (props.method === PROCUREMENT_METHOD_ORDER) {
            setStep(CREATE_ORDER_STEPS[0]);
        } else if (props.method === PROCUREMENT_METHOD_EXISTING_ORDER) {
            setStep(EXISTING_ORDER_STEPS[0]);
        }
    }, [props.method]);

    const toggleStep = (expanded: boolean, key?: string) => {
        if (expanded && key) {
            setStep(key);
        } else {
            setStep(STEP_NONE);
        }
    }

    if (props.method === PROCUREMENT_METHOD_DEMAND) {
        return (
            <div>
                <QuantityStep
                    quantity={props.quantity}
                    onChange={props.onChangeQuantity}
                    stepNumber={1}
                    step={step}
                    setStep={setStep}
                />
                <SelectVendorStep
                    product={props.product}
                    selectedVendor={props.selectedVendor}
                    onSelectVendor={props.onSelectVendor}
                    stepNumber={2}
                    step={step}
                    setStep={setStep}
                    disabled={props.quantity == null}
                />
                <UserDetailsStep
                    company={props.userDetails}
                    onChange={props.onChangeUserDetails}
                    stepNumber={3}
                    step={step}
                    setStep={setStep}
                    disabled={props.selectedVendor == null || props.quantity == null}
                />
                <ShippingDetailsStep
                    company={props.shippingDetails}
                    onChange={props.onChangeShippingDetails}
                    stepNumber={4}
                    step={step}
                    setStep={setStep}
                    disabled={props.selectedVendor == null || props.quantity == null}
                />
            </div>
        )
    } else if (props.method === PROCUREMENT_METHOD_ORDER) {
        return (
            <div>
                <QuantityStep
                    quantity={props.quantity}
                    onChange={props.onChangeQuantity}
                    stepNumber={1}
                    step={step}
                    setStep={setStep}
                />
                <SelectVendorStep
                    product={props.product}
                    selectedVendor={props.selectedVendor}
                    onSelectVendor={props.onSelectVendor}
                    stepNumber={2}
                    step={step}
                    setStep={setStep}
                    disabled={props.quantity == null}
                />
                <UserDetailsStep
                    company={props.userDetails}
                    onChange={props.onChangeUserDetails}
                    stepNumber={3}
                    step={step}
                    setStep={setStep}
                    disabled={props.selectedVendor == null || props.quantity == null}
                />
                <ShippingDetailsStep
                    company={props.shippingDetails}
                    onChange={props.onChangeShippingDetails}
                    stepNumber={4}
                    step={step}
                    setStep={setStep}
                    disabled={props.selectedVendor == null || props.quantity == null}
                />
                <OrderDetailsStep
                    details={props.order}
                    onChange={props.onChangeOrder}
                    stepNumber={5}
                    step={step}
                    setStep={setStep}
                    disabled={props.selectedVendor == null || props.quantity == null}
                />
            </div>
        )

    } else if (props.method === PROCUREMENT_METHOD_EXISTING_ORDER) {
        return (
            <div>
                <QuantityStep
                    quantity={props.quantity}
                    onChange={props.onChangeQuantity}
                    stepNumber={1}
                    step={step}
                    setStep={setStep}
                />
                <SelectVendorStep
                    product={props.product}
                    selectedVendor={props.selectedVendor}
                    onSelectVendor={props.onSelectVendor}
                    stepNumber={2}
                    step={step}
                    setStep={setStep}
                    disabled={props.quantity == null}
                />
                <FindExistingOrderStep
                    selectedOrderId={props.existingOrder}
                    onOrderSelected={props.onSelectExistingOrder}
                    stepNumber={3}
                    step={step}
                    setStep={setStep}
                    disabled={props.selectedVendor == null || props.quantity == null}
                    vendor={props.selectedVendor}
                />
                <UserDetailsStep
                    company={props.userDetails}
                    onChange={props.onChangeUserDetails}
                    stepNumber={4}
                    step={step}
                    setStep={setStep}
                    disabled={props.existingOrder == null || props.selectedVendor == null || props.quantity == null}
                />
            </div>
        )

    }
    return <div></div>
}

const QuantityStep: React.FC<{
    quantity?: number;
    onChange: (quantity?: number) => void;
    stepNumber?: number;
    step: string;
    setStep: (step: string) => void;
    disabled?: boolean;
}> = (props) => {
    return (
        <AccordionBox
            title={`${props.stepNumber || 1}. Set Quantity`}
            secondaryTitle={props.quantity !== undefined ? props.quantity.toString() : ''}
            isExpanded={props.step === STEP_SET_QUANTITY}
            onToggle={(expanded) => {
                if (expanded) {
                    props.setStep(STEP_SET_QUANTITY);
                } else {
                    props.setStep(STEP_NONE);
                }
            }}
            disabled={props.disabled}
        >
            <NumberField
                label='Quantity'
                value={props.quantity}
                onChange={props.onChange}
            />
        </AccordionBox>
    );
}

const SelectVendorStep: React.FC<{
    product: INormalizedProduct;
    selectedVendor?: string;
    onSelectVendor: (vendor?: string) => void;
    stepNumber?: number;
    step: string;
    setStep: (step: string) => void;
    disabled?: boolean;
}> = (props) => {
    return (
        <AccordionBox
            title={`${props.stepNumber || 2}. Select Vendor`}
            secondaryTitle={props.selectedVendor}
            isExpanded={props.step === STEP_SELECT_VENDOR}
            onToggle={(expanded) => {
                if (expanded) {
                    props.setStep(STEP_SELECT_VENDOR);
                } else {
                    props.setStep(STEP_NONE);
                }
            }}
            disabled={props.disabled}
        >
            <table className='data-table'>
                <thead>
                    <tr>
                        <th></th>
                        <th>Vendor</th>
                        <th>Cost</th>
                    </tr>
                </thead>
                <tbody>
                    {props.product.vendors?.map((vendor) => (
                        <tr
                            key={vendor.vendorName}
                            onClick={() => props.onSelectVendor(vendor.vendorName)}
                        >
                            <td><Checkbox checked={vendor.vendorName === props.selectedVendor} onChange={() => props.onSelectVendor(vendor.vendorName)} /></td>
                            <td>
                                <p style={{ fontWeight: 'bold', margin: 0 }}>
                                    {vendor.vendorName}
                                </p>
                                <p style={{ margin: 0 }}>{vendor.vendorPartNumber}</p>
                            </td>
                            <td>
                                <VendorDisplay
                                    product={props.product}
                                    vendor={[vendor]}
                                    dense
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </AccordionBox>
    );
}

const FindExistingOrderStep: React.FC<{
    selectedOrderId?: string;
    onOrderSelected: (orderId?: string) => void;
    stepNumber?: number;
    step: string;
    setStep: (step: string) => void;
    disabled?: boolean;
    vendor?: string;
}> = (props) => {
    return (
        <AccordionBox
            title={`${props.stepNumber || 3}. Existing Order`}
            isExpanded={props.step === STEP_EXISTING_ORDERS}
            onToggle={(expanded) => {
                if (expanded) {
                    props.setStep(STEP_EXISTING_ORDERS);
                } else {
                    props.setStep(STEP_NONE);
                }
            }}
            disabled={props.disabled}
        >
            <ExistingOrderSearch
                vendor={props.vendor}
                selectedOrderId={props.selectedOrderId}
                onOrderSelected={order => {
                    props.onOrderSelected(order.id);
                }}
            />
        </AccordionBox>
    );
}

const UserDetailsStep: React.FC<{
    company: IProcurementCompany;
    onChange: (company: IProcurementCompany) => void;
    stepNumber?: number;
    step: string;
    setStep: (step: string) => void;
    disabled?: boolean;
}> = (props) => {
    return (
        <AccordionBox
            title={`${props.stepNumber || 4}. User Details`}
            secondaryTitle={props.company.companyName}
            isExpanded={props.step === STEP_USER_DETAILS}
            onToggle={(expanded) => {
                if (expanded) {
                    props.setStep(STEP_USER_DETAILS);
                } else {
                    props.setStep(STEP_NONE);
                }
            }}
            disabled={props.disabled}
        >
            <CompanyForm
                company={props.company}
                onChange={props.onChange}
            />
        </AccordionBox>
    );
}

const ShippingDetailsStep: React.FC<{
    company: IProcurementCompany;
    onChange: (company: IProcurementCompany) => void;
    stepNumber?: number;
    step: string;
    setStep: (step: string) => void;
    disabled?: boolean;
}> = (props) => {
    return (
        <AccordionBox

            title={`${props.stepNumber || 5}. Shipping Details`}
            secondaryTitle={props.company.companyName}
            isExpanded={props.step === STEP_SHIPPING_DETAILS}
            onToggle={(expanded) => {
                if (expanded) {
                    props.setStep(STEP_SHIPPING_DETAILS);
                } else {
                    props.setStep(STEP_NONE);
                }
            }}
            disabled={props.disabled}
        >
            <CompanyForm
                company={props.company}
                onChange={props.onChange}
            />
        </AccordionBox>
    );
}

const OrderDetailsStep: React.FC<{
    details: Partial<ICreateProcurementOrderRequest>;
    onChange: (details: Partial<ICreateProcurementOrderRequest>) => void;
    stepNumber?: number;
    step: string;
    setStep: (step: string) => void;
    disabled?: boolean;
}> = (props) => {
    return (
        <AccordionBox
            title={`${props.stepNumber || 6}. Order Details`}
            isExpanded={props.step === STEP_ORDER_DETAILS}
            onToggle={(expanded) => {
                if (expanded) {
                    props.setStep(STEP_ORDER_DETAILS);
                } else {
                    props.setStep(STEP_NONE);
                }
            }}
            disabled={props.disabled}
        >
            <DataField
                label='Order Name'
                value={props.details.name}
                onChange={(ev, newValue) => props.onChange({ ...props.details, name: newValue })}
                />
            <DataField
                label='Purchase Order Number'
                value={props.details.purchaseOrderNumber}
                onChange={(ev, newValue) => props.onChange({ ...props.details, purchaseOrderNumber: newValue })}
            />
            <DataField
                label='Internal Notes'
                value={props.details.internalOrderNotes}
                onChange={(ev, newValue) => props.onChange({ ...props.details, internalOrderNotes: newValue })}
                multiline
                toolTip='Order notes internal to your company and not shared with the vendor or customer'
            />
            <DataField
                label='Vendor Notes'
                value={props.details.vendorNotes}
                onChange={(ev, newValue) => props.onChange({ ...props.details, vendorNotes: newValue })}
                multiline
                toolTip='Notes for the vendor regarding this order. Sent to supported vendors via extension when placing order.'
            />
            <ToggleField
                label='Allow Backorder'
                checked={props.details.allowBackorder || false}
                onChange={(ev, checked) => props.onChange({ ...props.details, allowBackorder: checked })}
                toolTip='For supported vendors, if selected, items that are out of stock will be backordered instead of cancelling them.'
            />
        </AccordionBox>
    );
}


export default ProcureProductPanel;